import type { CargoSpec } from "./Carry";
import type { ChapterId, ChapterInfo, SaveData } from "./types";

export const CHAPTERS: Record<ChapterId, ChapterInfo> = {
  prologue: {
    id: "prologue",
    number: "PROLOGUE",
    title: "Le dernier bon câble",
    subtitle: "Châteauguay, 18 h 42. Le drame est déjà en retard.",
  },
  parking: {
    id: "parking",
    number: "ACTE I",
    title: "Le purgatoire asphalté",
    subtitle: "Trouver une place avant la fin de l’événement.",
  },
  registration: {
    id: "registration",
    number: "ACTE II",
    title: "Badge ou mourir",
    subtitle: "Le courriel de confirmation existe. Quelque part.",
  },
  checkpoint: {
    id: "checkpoint",
    number: "ACTE III",
    title: "Opération sacoche",
    subtitle: "Tout ce qui gargouille n’est pas un watercooling.",
  },
  hall: {
    id: "hall",
    number: "ACTE IV",
    title: "Le désert RGB",
    subtitle: "Mille écrans. Zéro rallonge disponible.",
  },
  seat: {
    id: "seat",
    number: "ACTE V",
    title: "Le siège du siège",
    subtitle: "Quelqu’un est assis à ta place. Il mange des Doritos.",
  },
  network: {
    id: "network",
    number: "FINALE",
    title: "DHCPocalypse",
    subtitle: "La prophétie du câble Cat-5e s’accomplit.",
  },
  credits: {
    id: "credits",
    number: "ÉPILOGUE",
    title: "GG, probablement",
    subtitle: "La vraie victoire, c’était le ping rencontré en chemin.",
  },
};

export const DEFAULT_SAVE: SaveData = {
  version: 1,
  chapter: "prologue",
  checkpoint: 0,
  heroClass: "veteran",
  stats: {
    energy: 100,
    suspicion: 4,
    reputation: 0,
    score: 0,
    strikes: 0,
  },
  // Gisèle n'est pas « dans » l'inventaire au départ : elle est posée par terre
  // et pèse dix-huit kilos. Il faut aller la chercher.
  inventory: ["courriel-imprimé"],
  lore: [],
  flags: {},
  startedAt: Date.now(),
  elapsedSeconds: 0,
};

export const LORE = {
  parking: {
    title: "Archive 01 — La Grande Remorque",
    body: "En 2011, un participant tenta d’arriver avec son bureau complet sur une remorque. Il obtint le stationnement N-12, à onze minutes de marche. La légende dit qu’il fait encore l’aller-retour pour chercher sa souris.",
  },
  lobby: {
    title: "Archive 02 — Le badge maudit",
    body: "Le badge 404 fut imprimé une seule fois. Il donne accès à toutes les zones, sauf celle où son propriétaire se trouve. Trois bénévoles ont juré de ne jamais le réimprimer; un quatrième a juré tout court.",
  },
  checkpoint: {
    title: "Archive 03 — Le traité des liquides",
    body: "Après l’incident de la soupe RAM, tout liquide de plus de 100 ml doit posséder un pilote signé. Le règlement ne mentionne nulle part les tours beiges, ce qui est considéré comme un oubli historique.",
  },
  hallA: {
    title: "Archive 04 — La nuit du 56k",
    body: "Les vétérans racontent qu’autrefois, télécharger une mise à jour prenait une fin de semaine complète, et qu’il fallait supplier quelqu’un de ne pas utiliser le téléphone. Personne ne les croit, ce qui les rend plus forts.",
  },
  hallB: {
    title: "Archive 05 — Le fantôme du rang G",
    body: "À 3 h 14, une chaise vide se met parfois à crier « QUI A PRIS MON ADAPTATEUR? ». Elle n’a jamais eu d’adaptateur. Les organisateurs ont réglé le problème en retirant l’étiquette du rang G. Ça n’a rien réglé.",
  },
  seat: {
    title: "Archive 06 — Le Code du siège",
    body: "Article 1 : une place réservée demeure réservée. Article 2 : un sac de croustilles n’est pas une réservation, même saveur ketchup. Article 3 : celui qui déplace un manteau assume les conséquences émotionnelles.",
  },
  network: {
    title: "Archive 07 — Gérard-DHCP",
    body: "Le premier routeur du LAN obtint la conscience après avoir attribué deux fois 192.168.0.42. Il ne pardonna jamais à l’humanité, et surtout pas à celui qui a branché les deux bouts du même câble dans le même switch.",
  },
  diable: {
    title: "Archive 08 — Le Diable de 2014",
    body: "Un seul diable circule dans le stationnement depuis 2014. Personne ne l’a acheté, personne ne le ramène. Le porter revient à accepter que douze inconnus te demandent de l’emprunter « deux minutes ».",
  },
  multiprise: {
    title: "Archive 09 — La Guerre des multiprises",
    body: "Édition 2017 : quarante-deux joueurs ont branché quarante-deux multiprises les unes dans les autres. La rangée F a connu quatre secondes de gloire absolue avant l’obscurité totale. On appelle ça « le Grand Silence ».",
  },
  registre: {
    title: "Archive 10 — Le Registre des places volées",
    body: "Un cahier à spirale tenu par un bénévole anonyme recense chaque place volée depuis 2009. Il compte 1 118 entrées. La colonne « excuse invoquée » est remplie à 94 % par la phrase : « je pensais que c’était libre ».",
  },
} as const;

/**
 * Tout ce qui se transporte à bout de bras. `bulk` se compte sur trois : Gisèle
 * prend les deux bras à elle seule, l'écran en prend un et demi, le bac finit
 * de remplir. C'est ce qui force les allers-retours jusqu'au coffre.
 */
export const CARGO: Record<string, CargoSpec> = {
  "tour-gisèle": {
    id: "tour-gisèle",
    label: "Soulever Gisèle (18 kg)",
    short: "Gisèle",
    bulk: 3,
    rig: "tower",
    essential: true,
  },
  "moniteur-crt": {
    id: "moniteur-crt",
    label: "Prendre l’écran cathodique",
    short: "Écran 19 po",
    bulk: 2,
    rig: "screen",
    essential: true,
  },
  "bac-peripheriques": {
    id: "bac-peripheriques",
    label: "Prendre le bac de périphériques",
    short: "Périphériques",
    bulk: 1,
    rig: "crate",
    essential: true,
  },
  "chaise-gamer": {
    id: "chaise-gamer",
    label: "Prendre la chaise héritée",
    short: "Chaise",
    bulk: 2,
    rig: "box",
    essential: false,
  },
  "multiprise-maison": {
    id: "multiprise-maison",
    label: "Prendre la multiprise",
    short: "Multiprise",
    bulk: 1,
    rig: "box",
    essential: false,
  },
  glaciere: {
    id: "glaciere",
    label: "Prendre la glacière",
    short: "Glacière",
    bulk: 1,
    rig: "box",
    essential: false,
  },
};

/** Le sac se porte sur le dos : il ne prend pas de bras, seulement du dos. */
export const BACKPACK_ID = "sac-a-dos";

/** Ce qu'on peut entasser dans le sac au frigo, et ce que ça coûte au contrôle. */
export const FRIDGE_PICKS = [
  {
    id: "punch-maison",
    icon: "🧃",
    name: "Punch maison",
    detail: "Dans un contenant de jus. Personne n’est dupe, tout le monde est complice.",
    risk: 24,
    bonus: 380,
  },
  {
    id: "glace-seche",
    icon: "🧊",
    name: "Sac de glace",
    detail: "Fondra depuis Châteauguay. Le sac fera un bruit de marée.",
    risk: 9,
    bonus: 150,
  },
  {
    id: "cafe-thermos",
    icon: "🥤",
    name: "Thermos de café",
    detail: "Rempli hier. Techniquement encore du café.",
    risk: 2,
    bonus: 110,
  },
  {
    id: "sandwich-mystere",
    icon: "🥪",
    name: "Sandwich mystère",
    detail: "Emballé par quelqu’un de bien intentionné, à une date inconnue.",
    risk: 4,
    bonus: 120,
  },
  {
    id: "eau",
    icon: "💧",
    name: "Bouteille d’eau",
    detail: "Le choix des gens qui prévoient. Suspect en soi.",
    risk: 0,
    bonus: 40,
  },
] as const;

/** Places dans le sac à dos, une fois qu'on le porte. */
export const BACKPACK_SLOTS = 3;

export const ITEM_LABELS: Record<string, string> = {
  "tour-gisèle": "Gisèle, tour beige 18 kg",
  "moniteur-crt": "Écran cathodique 19 po",
  "bac-peripheriques": "Bac de périphériques (clavier, souris, tapis)",
  "chaise-gamer": "Chaise de bureau héritée d’un oncle",
  "multiprise-maison": "Multiprise à six têtes",
  glaciere: "Glacière de boissons",
  "sac-a-dos": "Sac à dos (porté)",
  "cafe-thermos": "Thermos de café d’hier",
  "sandwich-mystere": "Sandwich mystère",
  "courriel-imprimé": "Courriel imprimé (sans QR)",
  diable: "Le Diable (chariot de 2014)",
  "punch-maison": "Punch maison en contenant de jus",
  "glace-seche": "Sac de glace fondant vite",
  "gaffer-tape": "Ruban gaffer de la gang",
  "video-preuve": "Vidéo de la caméra 12",
  "multiprise-secrete": "Multiprise cachée sous le manteau",
  "ticket-parking": "Ticket P-404",
  "telephone-charge": "Téléphone à 3 %",
  badge: "Badge Packet_Pat",
  "rhum-pouch": "Rhum Pouch +3 moral",
  "mana-bleue": "2 canettes Mana Bleue",
  flasque: "Flasque « patch notes »",
  deodorant: "Déodorant (preuve troublante)",
  eau: "Bouteille d’eau réglementaire",
  chips: "Croustilles alibi",
  "cable-ethernet": "Câble Cat-5e ancestral",
  rallonge: "Rallonge orange certifiée",
  cafe: "Café de distributeur",
  "preuve-reservation": "Capture d’écran de la place 404",
  "usb-linux": "USB Linux mystérieuse",
  "lampe-frontale": "Lampe frontale à 11 lumens",
  "tournevis-gobelin": "Tournevis gobelin accordé en RJ45",
};

export const STORAGE_KEY = "snitch-lan-save-v1";

export const CHAPTER_ORDER: ChapterId[] = [
  "prologue",
  "parking",
  "registration",
  "checkpoint",
  "hall",
  "seat",
  "network",
  "credits",
];
